import { authClient } from '@/lib/auth-client.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { GoogleLogoIcon } from '@phosphor-icons/react';
import { WEB_BASE_URL } from '@/common/config/runtime.ts';

const formSchema = z.object({
  email: z.email().nonempty({ error: 'A valid email is required' }),
  password: z.string().nonempty({ error: 'A valid password is required' }),
});

type FormValues = z.infer<typeof formSchema>;

export const Route = createFileRoute('/auth/login')({
  component: RouteComponent,
  validateSearch: z.object({ error: z.string().optional() }),
});

function getErrorMessage(errorCode: string): string {
  if (errorCode.trim() === '') return '';
  if (errorCode === 'signup_disabled') {
    return 'You do not have access to this application';
  } else {
    return 'Something went wrong, please try again';
  }
}

function RouteComponent() {
  const { error } = Route.useSearch();
  const [loginError, setLoginError] = useState(getErrorMessage(error ?? ''));
  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (values: FormValues) => {
    setLoginError('');

    const result = await authClient.signIn.email({
      email: values.email,
      password: values.password,
    });

    setLoginError(result.error?.message ?? '');
  };

  const onGoogleLogin = async () => {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: WEB_BASE_URL,
      errorCallbackURL: `${WEB_BASE_URL}/auth/login`,
    });
  };

  const isBusy = isLoading || isSubmitting;

  return (
    <Box
      mih="100vh"
      px="md"
      py="xl"
      style={{
        display: 'grid',
        placeItems: 'center',
        background:
          'radial-gradient(circle at 20% 15%, rgba(59, 130, 246, 0.12), transparent 42%), radial-gradient(circle at 80% 85%, rgba(16, 185, 129, 0.12), transparent 40%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
      }}
    >
      <Paper
        radius="md"
        shadow="lg"
        p="xl"
        withBorder
        w="100%"
        maw={420}
        style={{ borderColor: 'rgba(148, 163, 184, 0.35)' }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={2}>Welcome back</Title>
              <Text c="dimmed" size="sm">
                Sign in to continue to your workspace.
              </Text>
            </Stack>

            <TextInput
              label="Email"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              disabled={isBusy}
              {...register('email')}
            />
            <TextInput
              label="Password"
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              disabled={isBusy}
              {...register('password')}
            />

            {loginError ? (
              <Text size="sm" c="red">
                {loginError}
              </Text>
            ) : null}

            <Button type="submit" loading={isBusy}>
              {isBusy ? 'Signing in...' : 'Sign In'}
            </Button>

            <Divider label="Or continue with Google" labelPosition="center" />

            <Button
              leftSection={<GoogleLogoIcon weight="fill" />}
              onClick={onGoogleLogin}
              loading={isBusy}
            >
              {isBusy ? 'Signing in...' : 'Sign In with Google'}
            </Button>

            {/*<Text size="sm" ta="center" c="dimmed">*/}
            {/*  Trouble signing in?{' '}*/}
            {/*  <Anchor href="mailto:support@pac.com">Contact support</Anchor>*/}
            {/*</Text>*/}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
